import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetCarriers } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import CarrierHttpService from '../../../services/http/masterData/carrierHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import CarrierForm from './form/CarrierForm';
import { setCarrierMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { ConfirmationDialog, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../@types/other';
import { useLocation } from 'react-router-dom';
import UserHelper from '../../../services/helper/userHelper';
import CarrierModelHelper from '../../../@types/model/masterData/carrier/carrierModelHelper';
import { ICarrierFormValues } from '../../../@types/model/masterData/carrier/carrierFormValues';
import { ICarrier } from '../../../@types/model/masterData/carrier/carrier';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import { CustomTable } from '../../../components/table/CustomTableWrapper';

const CarrierScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const carriers = useAppSelector(x => x.masterData.carriers);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedCarrier, setSelectedCarrier] = useState<ICarrier | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deletingCarrier, setDeletingCarrier] = useState<ICarrier | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await CarrierHttpService.getCarrierData();
                    dispatch(dataSetCarriers(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError | undefined;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading carriers.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const onSubmit = async (values : ICarrierFormValues) => {
        setIsLoading(true);

        try {
            const upsert = CarrierModelHelper.createUpsert(values);
            const res = await CarrierHttpService.addOrUpdateCarrier(upsert);
            const updatedList = upsertArrayElement(carriers, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetCarriers(updatedList));
            await setCarrierMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedCarrier ? 'Carrier updated successfully.' : 'Carrier added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating carrier data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeCarrier = async () => {
        if (!deletingCarrier) return;
        const updatedCarrier = { ...deletingCarrier, isActive: false };
        setIsLoading(true);
        try {
            const res = await CarrierHttpService.deleteCarrier(updatedCarrier.id);
            const updatedList = upsertArrayElement(carriers, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetCarriers(updatedList));
            await setCarrierMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Carrier deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting carrier.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedCarrier(null);
    };

    const edit = (selectedRow : ICarrier) => {
        setSelectedCarrier(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : ICarrier) : void => {
        setDeletingCarrier(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingCarrier(null);
    };

    const deleteButtonTooltip = (row : ICarrier) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Carrier', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return CarrierModelHelper.createFormValues(selectedCarrier);
        }
    }, [selectedCarrier, isDialogOpen]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    /*================================================================================================================
    *                                                  Render Methods
    * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<ICarrier>
                    
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Carrier', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={!userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Carrier', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : ICarrier) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        fitWidthToPage
                        rows={carriers}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedCarrier ? 'Edit Carrier' : 'Add Carrier'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <CarrierForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Carrier'}
                open={!!deletingCarrier}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeCarrier}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default CarrierScreen;
